import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import { flatMapDeep, flattenDeep, isEqual, last, uniqWith } from 'lodash-es';
import { isNil } from 'rambda';
import { z } from 'zod';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import { generateListResponseScheme, type ListRespondedWith } from '@/models/response';
import { fetcher } from '@/utils/fetcher';
import { queryClient } from '@/utils/query-client';
import { stringifiedDateSchema } from '@/utils/schemas';

const recipientSchema = z.object({
  UserId: z.number(),
  GroupId: z.number(),
  RecipientName: z.string().nullable(),
  RecipientImg: z.string().nullable(),
});

export type Recipient = z.infer<typeof recipientSchema>;

const batchMessageSchema = z.object({
  BatchMessageId: z.number(),
  Message: z.string(),
  MsgType: z.nativeEnum(ConversationMessageContentType),
  CreateTime: stringifiedDateSchema,
  Recipients: recipientSchema.array(),
});

export type BatchMessage = z.infer<typeof batchMessageSchema>;

export const PAGE_SIZE = 30;

export const batchMessagesInfiniteQueryOptions = infiniteQueryOptions({
  queryKey: ['/GetBatchMessageHistory', 'infinite'],
  queryFn: async ({ pageParam: { LastBatchMessageId }, signal }) => {
    const response = await fetcher<ListRespondedWith<BatchMessage>>(
      '/GetBatchMessageHistory',
      { PageSize: PAGE_SIZE, LastBatchMessageId },
      {
        signal,
        schema: generateListResponseScheme(batchMessageSchema),
        validResponseCodes: [0],
      },
    );
    return response.Data?.map((d) => ({ ...d, Recipients: uniqWith(d.Recipients, isEqual) }));
  },

  getNextPageParam: (lastPage) => {
    if (!lastPage || lastPage.length === 0 || lastPage.length < PAGE_SIZE) return undefined;
    return { LastBatchMessageId: last(lastPage)?.BatchMessageId ?? 0 };
  },
  initialPageParam: { LastBatchMessageId: 0 },
  staleTime: Number.POSITIVE_INFINITY,
});

export const useInfiniteBatchMessages = () => {
  const query = useInfiniteQuery(batchMessagesInfiniteQueryOptions);
  const mapData = flatMapDeep(query?.data?.pages ?? [])
    .filter((item) => !isNil(item))
    .reverse()
    .reduce<Record<number, BatchMessage>>((acc, curr) => {
      acc[curr.BatchMessageId] = { ...curr };
      return acc;
    }, {});
  return { ...query, paginatedData: query.data, mapData: mapData };
};

export const getBatchMessagesMap = () => {
  const data = queryClient.getQueryData(batchMessagesInfiniteQueryOptions.queryKey);
  const dataArr = data?.pages.filter((item) => !isNil(item));

  return (
    flattenDeep(dataArr).reduce<Record<number, BatchMessage>>((acc, curr) => {
      acc[curr.BatchMessageId] = { ...curr };
      return acc;
    }, {}) ?? {}
  );
};
